import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useOutletContext, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, ImagePlus, CheckCircle2, AlertTriangle, Monitor, Smartphone, MessageCircle, Facebook, Send } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, SelectF, CheckF, ImageF, inputCls } from "../admin/AFields";
import StudioCard, { STYLE_THEMES, CARD_SIZES } from "./StudioCard";

const BLANK = { title: "", occasion: "", category: "Inspirational", date: new Date().toISOString().slice(0, 10),
  language: "bilingual", message: "", message_hi: "", quote: "", quote_author: "", quote_verified: false,
  image: "", sender_profile_id: "", person_id: "", style: "elegant", size: "1:1", theme: "", tone: "warm",
  whatsapp_message: "", fb_caption: "", hashtags: [], status: "draft", publish_at: "", is_daily: false };

const AI_STYLES = ["", "shorter", "formal", "inspirational"];

export default function StudioComposer() {
  const [params] = useSearchParams();
  const { base } = useOutletContext();
  const [draft, setDraft] = useState(BLANK);
  const [orgs, setOrgs] = useState([]);
  const [people, setPeople] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [ai, setAi] = useState({ theme: "", tone: "warm", audience: "general", length: "medium", instructions: "", model: "gpt-5.4-mini" });
  const [imgPrompt, setImgPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");

  useEffect(() => {
    api.get("/studio/orgs").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/studio/people").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/greetings/occasions").then((r) => setOccasions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = params.get("id");
    if (id) {
      api.get("/greetings/list").then((r) => {
        const g = r.data.find((x) => x.id === id);
        if (g) setDraft({ ...BLANK, ...g, publish_at: g.publish_at ? String(g.publish_at).slice(0, 16) : "" });
      }).catch(() => {});
    }
  }, [params]);

  const org = useMemo(() => orgs.find((o) => o.id === draft.sender_profile_id) || orgs.find((o) => o.is_default) || {}, [orgs, draft.sender_profile_id]);
  const person = useMemo(() => people.find((p) => p.id === draft.person_id) || null, [people, draft.person_id]);
  const set = (k) => (v) => setDraft({ ...draft, [k]: v });

  const buildPrompt = () =>
    `Occasion: ${draft.occasion || "Good Morning"}. Date: ${draft.date}. Organization: ${org.organisation || org.name || "Sahaj Sarathi Foundation"}. ` +
    `Theme: ${ai.theme || draft.category}. Tone: ${ai.tone}. Audience: ${ai.audience}. Length: ${ai.length}. ` +
    `${draft.quote ? "Include the provided quote." : ""} ${ai.instructions}`;

  const runAI = async (style = "", langOverride) => {
    setAiBusy(true);
    try {
      const r = await api.post("/ai/greeting", {
        prompt: buildPrompt(), language: langOverride || draft.language, style, model: ai.model,
        current: draft.message ? JSON.stringify({ heading: draft.title, message: draft.message }) : "",
      });
      const d = r.data;
      setDraft((prev) => ({ ...prev,
        title: d.heading || prev.title,
        message: (langOverride === "hindi" ? prev.message : d.message) || prev.message,
        message_hi: langOverride === "hindi" ? (d.message || prev.message_hi) : (d.message_hi || prev.message_hi),
        quote: d.quote ?? prev.quote, quote_author: d.quote_author ?? prev.quote_author,
        quote_verified: !!d.quote_verified,
        whatsapp_message: d.whatsapp_message || prev.whatsapp_message,
        fb_caption: d.social_caption || prev.fb_caption,
        hashtags: d.hashtags || prev.hashtags,
      }));
      toast.success("AI draft applied — review before publishing");
    } catch (e) { toast.error(errMsg(e)); } finally { setAiBusy(false); }
  };

  const genImage = async () => {
    if (!imgPrompt.trim()) { toast.error("Describe the image first"); return; }
    setImgBusy(true);
    try {
      const r = await api.post("/ai/image", { prompt: imgPrompt, aspect: draft.size === "landscape" ? "landscape" : draft.size });
      setDraft({ ...draft, image: r.data.url });
      toast.success("AI image generated");
    } catch (e) { toast.error(errMsg(e)); } finally { setImgBusy(false); }
  };

  const checklist = [
    [!!draft.title?.trim(), "Headline present"],
    [!!draft.message?.trim(), "Message present"],
    [!!org?.name, "Organization profile selected"],
    [!!draft.date, "Date set"],
    [!(draft.quote && !draft.quote_author), draft.quote ? "Quote has attribution" : "Quote attribution (n/a)"],
    [!!(draft.image || draft.style), "Visual style/background set"],
  ];
  const canPublish = checklist[0][0] && checklist[1][0];

  const save = async (status) => {
    setBusy(true);
    try {
      const body = { ...draft, status: status || draft.status };
      if (body.status === "scheduled" && body.publish_at) body.publish_at = new Date(body.publish_at).toISOString();
      const r = await api.post("/greetings/save", body);
      setDraft({ ...body, id: r.data.id });
      toast.success(status === "published" ? "Greeting published" : status === "scheduled" ? "Greeting scheduled" : "Draft saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const previewWidths = { desktop: "w-full", mobile: "max-w-[380px]", whatsapp: "max-w-[420px]", facebook: "max-w-[500px]" };

  return (
    <div data-testid="studio-composer">
      <SEO title="Create Greeting" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Create Greeting</h1>

      <div className="mt-6 grid gap-8 xl:grid-cols-2">
        <div className="space-y-6">
          {/* AI GENERATOR */}
          <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="studio-ai-panel">
            <p className="flex items-center gap-2 text-sm font-bold text-navy"><Sparkles size={16} className="text-saffron-600" /> AI Greeting Generator</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <SelectF label="AI Model" value={ai.model} onChange={(v) => setAi({ ...ai, model: v })} options={["gpt-5.4-mini", "gpt-5.5", "gpt-5.6-terra", "claude-sonnet-5", "claude-sonnet-4-6", "claude-opus-4-8"]} />
              <SelectF label="Tone" value={ai.tone} onChange={(v) => setAi({ ...ai, tone: v })} options={["warm", "formal", "festive", "spiritual", "professional", "inspirational"]} />
              <SelectF label="Audience" value={ai.audience} onChange={(v) => setAi({ ...ai, audience: v })} options={["general", "volunteers", "donors", "clients", "advocates", "participants", "officials"]} />
              <SelectF label="Length" value={ai.length} onChange={(v) => setAi({ ...ai, length: v })} options={["short", "medium", "long"]} />
            </div>
            <div className="mt-4">
              <AreaF label="Special Instructions (optional)" value={ai.instructions} onChange={(v) => setAi({ ...ai, instructions: v })} rows={2} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => runAI()} disabled={aiBusy} className="btn-primary !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="studio-ai-generate">{aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate</button>
              <button onClick={() => runAI()} disabled={aiBusy} className="btn-dark !rounded-md !px-4 !py-2 text-xs" data-testid="studio-ai-regenerate">Regenerate</button>
              <button onClick={() => { const styles = Object.keys(STYLE_THEMES); const cur = styles.indexOf(draft.style); set("style")(styles[(cur + 1) % styles.length]); }} className="rounded-md border border-gray-300 px-3 py-2 text-xs font-bold text-navy" data-testid="studio-ai-another-style">Another Style</button>
              <button onClick={() => runAI("shorter")} disabled={aiBusy} className="rounded-md border border-gray-300 px-3 py-2 text-xs font-bold text-navy" data-testid="studio-ai-shorter">Make Shorter</button>
              <button onClick={() => runAI("formal")} disabled={aiBusy} className="rounded-md border border-gray-300 px-3 py-2 text-xs font-bold text-navy" data-testid="studio-ai-formal">More Formal</button>
              <button onClick={() => runAI("", "hindi")} disabled={aiBusy} className="rounded-md border border-gray-300 px-3 py-2 text-xs font-bold text-navy" data-testid="studio-ai-hindi">हिन्दी</button>
              <button onClick={() => runAI("", "english")} disabled={aiBusy} className="rounded-md border border-gray-300 px-3 py-2 text-xs font-bold text-navy" data-testid="studio-ai-english">English</button>
              <button onClick={() => runAI("", "bilingual")} disabled={aiBusy} className="rounded-md border border-gray-300 px-3 py-2 text-xs font-bold text-navy" data-testid="studio-ai-bilingual">Bilingual</button>
            </div>
          </div>

          {/* COMPOSER FIELDS */}
          <div className="space-y-5 rounded-lg border border-gray-200 bg-white p-5" data-testid="studio-fields">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Greeting For (Branding)</label>
                <select value={draft.sender_profile_id || org.id || ""} onChange={(e) => set("sender_profile_id")(e.target.value)} className={inputCls} data-testid="studio-branding">
                  {orgs.filter((o) => o.enabled !== false).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <p className="-mt-3 hidden text-xs text-slate-500 sm:block"></p>
            </div>
            <p className="-mt-3 text-xs font-semibold text-saffron-600">Using: {org.name} {org.website ? `· ${org.website}` : ""}</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Signatory (Person)</label>
                <select value={draft.person_id} onChange={(e) => set("person_id")(e.target.value)} className={inputCls} data-testid="studio-person">
                  <option value="">None</option>
                  {people.filter((p) => p.active !== false).map((p) => <option key={p.id} value={p.id}>{p.name} — {p.designation}</option>)}
                </select>
              </div>
              <SelectF label="Template Style" value={draft.style} onChange={set("style")} options={Object.keys(STYLE_THEMES)} />
              <SelectF label="Output Size" value={draft.size} onChange={set("size")} options={Object.keys(CARD_SIZES)} />
              <SelectF label="Language" value={draft.language} onChange={set("language")} options={["english", "hindi", "bilingual"]} />
            </div>
            <TextF label="Greeting Headline" value={draft.title} onChange={set("title")} required />
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Occasion</label>
                <input list="studio-occasions" value={draft.occasion} onChange={(e) => set("occasion")(e.target.value)} className={inputCls} data-testid="studio-occasion" />
                <datalist id="studio-occasions">{occasions.map((o) => <option key={o.id} value={o.name} />)}</datalist>
              </div>
              <TextF label="Date" type="date" value={draft.date} onChange={set("date")} />
            </div>
            <AreaF label="Message (English)" value={draft.message} onChange={set("message")} rows={3} ai contentType="greeting" />
            {draft.language !== "english" && <AreaF label="Message (Hindi)" value={draft.message_hi} onChange={set("message_hi")} rows={3} ai contentType="greeting" />}
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="Quote" value={draft.quote} onChange={set("quote")} />
              <TextF label="Quote Author" value={draft.quote_author} onChange={set("quote_author")} />
            </div>
            <CheckF label="Quote verified (never attribute unverified quotes)" value={draft.quote_verified} onChange={set("quote_verified")} />
            <div>
              <ImageF label="Background Image" value={draft.image} onChange={set("image")} />
              <div className="mt-3 flex gap-2">
                <input value={imgPrompt} onChange={(e) => setImgPrompt(e.target.value)} placeholder="AI image prompt, e.g. 'festive marigold background'" className={inputCls} data-testid="studio-img-prompt" />
                <button onClick={genImage} disabled={imgBusy} className="btn-dark shrink-0 !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="studio-img-generate">
                  {imgBusy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} AI Image
                </button>
              </div>
            </div>
            <AreaF label="WhatsApp Message" value={draft.whatsapp_message} onChange={set("whatsapp_message")} rows={2} />
            <AreaF label="Facebook Caption" value={draft.fb_caption} onChange={set("fb_caption")} rows={2} />
            <TextF label="Hashtags (space separated)" value={(draft.hashtags || []).join(" ")} onChange={(v) => set("hashtags")(v.split(/\s+/).filter(Boolean))} />
            <div className="grid gap-5 sm:grid-cols-2">
              <SelectF label="Status" value={draft.status} onChange={set("status")} options={["draft", "scheduled", "published", "archived"]} />
              {draft.status === "scheduled" && <TextF label="Publish At" type="datetime-local" value={draft.publish_at} onChange={set("publish_at")} />}
            </div>
            <CheckF label="Daily Good Morning greeting" value={draft.is_daily} onChange={set("is_daily")} />
          </div>

          {/* PRE-PUBLISH CHECK */}
          <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="studio-checklist">
            <p className="text-sm font-bold text-navy">Pre-Publish Check</p>
            <ul className="mt-3 space-y-1.5">
              {checklist.map(([ok, label], i) => (
                <li key={i} className={`flex items-center gap-2 text-xs font-semibold ${ok ? "text-brandgreen" : "text-red-500"}`}>
                  {ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {label}
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
              <button onClick={() => save("draft")} disabled={busy} className="rounded-md border border-gray-200 px-4 py-2 text-xs font-bold text-navy" data-testid="studio-save-draft">Save Draft</button>
              <button onClick={() => save("scheduled")} disabled={busy || !canPublish} className="btn-dark !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="studio-schedule">Schedule</button>
              <button onClick={() => save("published")} disabled={busy || !canPublish} className="btn-primary !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="studio-publish">
                {busy && <Loader2 size={13} className="animate-spin" />} Approve & Publish
              </button>
              <Link to={draft.id ? `${base}/sender?greeting_id=${draft.id}` : "#"} onClick={(e) => { if (!draft.id) { e.preventDefault(); toast.info("Save the draft first, then send on WhatsApp"); } }}
                className={`inline-flex items-center gap-1.5 rounded-md bg-[#25D366] px-4 py-2 text-xs font-bold text-white ${!draft.id ? "cursor-not-allowed opacity-50" : "hover:brightness-95"}`} data-testid="studio-send-whatsapp">
                <Send size={13} /> Send on WhatsApp
              </Link>
            </div>
          </div>
        </div>

        {/* PREVIEW */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Preview:</span>
            {[["desktop", Monitor], ["mobile", Smartphone], ["whatsapp", MessageCircle], ["facebook", Facebook]].map(([m, Icon]) => (
              <button key={m} onClick={() => setPreviewMode(m)} data-testid={`preview-${m}`}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold capitalize transition-colors ${previewMode === m ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>
                <Icon size={13} /> {m}
              </button>
            ))}
          </div>
          <div className={`${previewWidths[previewMode]} mx-auto transition-[max-width] duration-300`}>
            <StudioCard greeting={draft} org={org} person={person} style={draft.style} size={draft.size} />
          </div>
        </div>
      </div>
    </div>
  );
}
